import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, Utensils, Camera, Globe, Heart } from 'lucide-react';

function Home() {
  // Language state with localStorage persistence
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  // Persist language choice
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  // Translations
  const translations = {
    en: {
      header: {
        date: 'November 8, 2025',
        venue: 'Conservatorio de Música de Puerto Rico, San Juan'
      },
      navigation: {
        title: 'Wedding Information'
      },
      pages: {
        thingsToDo: {
          title: 'Things To Do',
          description: 'Explore our favorite spots in Puerto Rico'
        },
        guestList: {
          title: 'Guest List',
          description: 'See who\'s attending our special day'
        },
        seating: {
          title: 'Seating Chart',
          description: 'Find your table at the reception'
        },
        photos: {
          title: 'Photo Album',
          description: 'Share and view photos from the celebration'
        },
        website: {
          title: 'Wedding Website',
          description: 'RSVP and view more details'
        }
      },
      buttons: {
        explore: 'Explore',
        comingSoon: 'Coming Soon'
      },
      details: {
        title: 'Wedding Day Details',
        date: 'Date',
        dateText: 'Saturday, November 8, 2025',
        venue: 'Venue',
        venueName: 'Conservatorio de Música de PR',
        venueAddress: '951 Ave Ponce de León, Santurce',
        attire: 'Attire',
        attireText: 'Semi-Formal'
      },
      footer: {
        message: 'We can\'t wait to celebrate with you in Puerto Rico!'
      }
    },
    es: {
      header: {
        date: '8 de Noviembre, 2025',
        venue: 'Conservatorio de Música de Puerto Rico, San Juan'
      },
      navigation: {
        title: 'Información de la Boda'
      },
      pages: {
        thingsToDo: {
          title: 'Qué Hacer',
          description: 'Explora nuestros lugares favoritos en Puerto Rico'
        },
        guestList: {
          title: 'Lista de Invitados',
          description: 'Mira quién asistirá a nuestro día especial'
        },
        seating: {
          title: 'Mapa de Asientos',
          description: 'Encuentra tu mesa en la recepción'
        },
        photos: {
          title: 'Álbum de Fotos',
          description: 'Comparte y mira fotos de la celebración'
        },
        website: {
          title: 'Página Web de la Boda',
          description: 'RSVP y ver más detalles'
        }
      },
      buttons: {
        explore: 'Explorar',
        comingSoon: 'Próximamente'
      },
      details: {
        title: 'Detalles del Día de la Boda',
        date: 'Fecha',
        dateText: 'Sábado, 8 de Noviembre, 2025',
        venue: 'Lugar',
        venueName: 'Conservatorio de Música de PR',
        venueAddress: '951 Ave Ponce de León, Santurce',
        attire: 'Vestimenta',
        attireText: 'Semi-Formal'
      },
      footer: {
        message: '¡No podemos esperar para celebrar con ustedes en Puerto Rico!'
      }
    }
  };

  const t = translations[language];

  const pages = [
    {
      title: t.pages.thingsToDo.title,
      description: t.pages.thingsToDo.description,
      icon: MapPin,
      path: '/things-to-do',
      available: true,
      color: '#C87941'
    },
    {
      title: t.pages.seating.title,
      description: t.pages.seating.description,
      icon: Utensils,
      path: '/seating',
      available: true,
      color: '#B8956A'
    },
    {
      title: t.pages.photos.title,
      description: t.pages.photos.description,
      icon: Camera,
      path: 'https://photos.app.goo.gl/7guWe3JtTzt5UkGY8',
      available: true,
      external: true,
      color: '#5B8C5A'
    },
    {
      title: t.pages.website.title,
      description: t.pages.website.description,
      icon: Heart,
      path: 'https://withjoy.com/danielle-and-fernando-nov-25',
      available: true,
      external: true,
      color: '#7B9EA8'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDF8F3' }}>
      {/* Header */}
      <div className="text-center py-16 px-4 relative" style={{ backgroundColor: '#C87941', color: 'white' }}>
        {/* Language Toggle Button */}
        <button
          onClick={toggleLanguage}
          className="absolute top-4 right-4 flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-gray-800 hover:bg-gray-100 transition-colors shadow-lg"
          aria-label="Toggle language"
        >
          <Globe size={20} style={{ color: '#C87941' }} />
          <span className="font-semibold" style={{ color: '#C87941' }}>
            {language === 'en' ? 'Español' : 'English'}
          </span>
        </button>

        <h1 className="text-5xl font-bold mb-4">Danielle & Fernando</h1>
        <p className="text-xl mb-2">{t.header.date}</p>
        <p className="text-lg">{t.header.venue}</p>
      </div>

      {/* Navigation Cards */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#C87941' }}>
          {t.navigation.title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {pages.map((page) => {
            const Icon = page.icon;

            if (page.available) {
              const CardContent = (
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div
                      className="p-3 rounded-full mr-4"
                      style={{ backgroundColor: page.color + '20' }}
                    >
                      <Icon size={32} style={{ color: page.color }} />
                    </div>
                    <h3 className="text-2xl font-bold" style={{ color: page.color }}>
                      {page.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-lg">{page.description}</p>
                  <div className="mt-4 inline-flex items-center text-sm font-semibold" style={{ color: page.color }}>
                    {t.buttons.explore} →
                  </div>
                </div>
              );

              if (page.external) {
                return (
                  <a
                    key={page.path}
                    href={page.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
                  >
                    {CardContent}
                  </a>
                );
              } else {
                return (
                  <Link
                    key={page.path}
                    to={page.path}
                    className="block bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
                  >
                    {CardContent}
                  </Link>
                );
              }
            } else {
              return (
                <div
                  key={page.path}
                  className="block bg-white rounded-lg shadow-lg overflow-hidden opacity-60 cursor-not-allowed"
                >
                  <div className="p-8">
                    <div className="flex items-center mb-4">
                      <div
                        className="p-3 rounded-full mr-4"
                        style={{ backgroundColor: page.color + '20' }}
                      >
                        <Icon size={32} style={{ color: page.color }} />
                      </div>
                      <h3 className="text-2xl font-bold" style={{ color: page.color }}>
                        {page.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-lg">{page.description}</p>
                    <div className="mt-4 inline-flex items-center text-sm font-semibold text-gray-400">
                      {t.buttons.comingSoon}
                    </div>
                  </div>
                </div>
              );
            }
          })}
        </div>

        {/* Wedding Details */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: '#C87941' }}>
            {t.details.title}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#C87941' }}>{t.details.date}</h4>
              <p className="text-gray-700">{t.details.dateText}</p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#C87941' }}>{t.details.venue}</h4>
              <p className="text-gray-700">{t.details.venueName}</p>
              <p className="text-gray-600 text-sm">{t.details.venueAddress}</p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#C87941' }}>{t.details.attire}</h4>
              <p className="text-gray-700">{t.details.attireText}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-600">
          <p className="mb-2">{t.footer.message}</p>
          <p className="text-sm">🌴 ☀️ 🥂</p>
        </div>
      </div>
    </div>
  );
}

export default Home;
